import {
  Account,
  AccountGroup,
  AdvertisingAccountType,
} from '@amzn/aac-chrome-internal-api';

/**
 * This function finds the global account or account to be highlighted in the account switcher.
 * The reason this is needed is because a global account can contain multiple accounts in different
 * countries.  We still want to high light the global account that the user is using.
 */
export const findSelectedAccountSwitcherAccount = (
  currentEntityId: string,
  accounts: Account[],
): Account | undefined => {
  for (const account of accounts) {
    // Case when currentEntityId matches global account
    if (account.entityId === currentEntityId) {
      return account;
    }

    // Case when currentEntityId matches an account under a global account.
    // We return the global account id in this case
    if (
      account.nestedAccounts &&
      Array.isArray(account.nestedAccounts) &&
      findSelectedAccountSwitcherAccount(
        currentEntityId,
        account.nestedAccounts,
      ) !== undefined
    ) {
      return account;
    }
  }

  return undefined;
};

/**
 * Accounts carry `accountType` on the wire (confirmed against the live Trim
 * response) though the shared `Account` type doesn't declare it. Type it as an
 * optional local field rather than widening the published type.
 */
type MemberWithAccountType = Account & { accountType?: AdvertisingAccountType };

/**
 * Choose the entity a global account switches into from its (possibly
 * merged-across-pages) members. Mirrors Trim
 * `AccountResultViewMapper.resolveGlobalAccountView`: prefer a SPONSORED_ADS
 * member in the current marketplace, then any current-marketplace member;
 * otherwise keep `fallbackUrl` — Trim's global-interstitial URL, carried over
 * from whichever page produced the global-account row. So a current-marketplace
 * member arriving on a later page upgrades an earlier interstitial fallback.
 *
 * `accountType` is optional (see {@link MemberWithAccountType}); when absent the
 * SPONSORED_ADS tiebreak falls back to the first current-marketplace member,
 * which is the same target whenever a global account has one member per
 * marketplace (the normal case).
 */
const amendUrlIfNeeded = (
  members: MemberWithAccountType[],
  currentMarketplaceId: string | undefined,
  fallbackUrl: string,
): string => {
  if (!currentMarketplaceId) return fallbackUrl;
  const currentMarketplaceMembers = members.filter(
    (m) => m.marketplaceId === currentMarketplaceId,
  );

  if (currentMarketplaceMembers.length === 0) {
    return fallbackUrl;
  }

  const sponsoredAds = currentMarketplaceMembers.find(
    (m) => m.accountType === AdvertisingAccountType.SPONSORED_ADS,
  );

  return (sponsoredAds ?? currentMarketplaceMembers[0]).url;
};

const mergeAccounts = (
  currentAccounts: Account[],
  nextPage: Account[],
  currentMarketplaceId?: string,
): Account[] => {
  const combinedSelection = [...currentAccounts, ...nextPage];

  // Fold accounts sharing an entityId (a global account whose members span
  // pages) into one row, keeping arrival order. Rows without an entityId can't
  // be keyed, so they pass through unchanged rather than being dropped. An array
  // (not a keyed object) preserves order — a plain object would reorder
  // numeric-like entityIds and break Trim's group/label ordering.
  const merged: Account[] = [];
  const indexByEntityId = new Map<string, number>();
  for (const account of combinedSelection) {
    const seenIndex = account.entityId
      ? indexByEntityId.get(account.entityId)
      : undefined;
    if (seenIndex === undefined) {
      if (account.entityId) {
        indexByEntityId.set(account.entityId, merged.length);
      }
      merged.push({ ...account });
    } else {
      const existing = merged[seenIndex];
      merged[seenIndex] = {
        ...existing,
        nestedAccounts: [
          ...(existing.nestedAccounts ?? []),
          ...(account.nestedAccounts ?? []),
        ],
      };
    }
  }

  return merged.map((account) => ({
    ...account,
    url: amendUrlIfNeeded(
      account.nestedAccounts ?? [],
      currentMarketplaceId,
      account.url,
    ),
  }));
};

export const mergeAccountGroups = (
  current: AccountGroup[],
  nextPage: AccountGroup[],
  currentMarketplaceId?: string,
): AccountGroup[] => {
  const accountTypeMapper = ({ accountType }: AccountGroup) => accountType;

  // Extract all the accountTypes across current selection and next page that was
  // retrieved
  const accountTypes = new Set([
    ...current.map(accountTypeMapper),
    ...nextPage.map(accountTypeMapper),
  ]);

  const combinedSelection = Array.from(accountTypes).reduce<
    Record<string, AccountGroup>
  >((selection, type) => {
    const currentGroup = current.find((group) => group.accountType === type);
    const nextGroup = nextPage.find((group) => group.accountType === type);
    const group = currentGroup ?? nextGroup;
    if (!group) return selection;
    selection[type] = {
      ...group,
      accounts: mergeAccounts(
        currentGroup?.accounts ?? [],
        nextGroup?.accounts ?? [],
        currentMarketplaceId,
      ),
    };
    return selection;
  }, {});

  return Object.values(combinedSelection);
};

export const getSingleGlobalAccount = (
  accounts: AccountGroup[],
  entityId: string,
): Account | undefined => {
  const singleGlobalAccountGroup = accounts?.find(
    (accountGroup) =>
      accountGroup.accountType === AdvertisingAccountType.GLOBAL,
  );
  if (!singleGlobalAccountGroup) {
    return undefined;
  }
  return findSelectedAccountSwitcherAccount(
    entityId,
    singleGlobalAccountGroup.accounts,
  );
};

export const getGlobalSponsoredAdsAccountFromAccountGroup = (
  currentEntityId: string,
  currentEntityType: AdvertisingAccountType,
  accounts: AccountGroup[],
): Account | undefined => {
  if (currentEntityType !== AdvertisingAccountType.SPONSORED_ADS) {
    return undefined;
  }

  const sponsoredAdsAccountGroup = accounts.find(
    (accountGroup) =>
      accountGroup.accountType === AdvertisingAccountType.SPONSORED_ADS,
  );

  // this case should never happen if the currentEntityType is SPONSORED_ADS
  if (!sponsoredAdsAccountGroup) {
    return undefined;
  }

  const topLevelAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    sponsoredAdsAccountGroup.accounts,
  );

  // we determine if it is a global account if there is an account in the nestedAccounts prop
  return topLevelAccount &&
    Array.isArray(topLevelAccount.nestedAccounts) &&
    topLevelAccount.nestedAccounts.length > 0
    ? topLevelAccount
    : undefined;
};

export const updateMarketplaceLabelWithRetailerName = (
  marketplaceLabel: string,
  retailerName: string,
): string => {
  if (!retailerName) {
    return marketplaceLabel;
  }

  // Only Sponsored ads (Brand) accounts can have an associated retailerName.
  // Sponsored ads accounts always contain a marketplace name in the marketplaceLabel.
  // Therefore, we need to insert the retailer name between the account type name and marketplace name
  const marketplaceLabelArr = marketplaceLabel.split(', ');
  marketplaceLabelArr.splice(1, 0, retailerName);
  return marketplaceLabelArr.join(', ');
};

/**
 * Query parameters that identify entity context.
 * These are stripped from the URL when switching to "All Accounts" mode.
 */
export const ENTITY_CONTEXT_PARAMS: readonly string[] = [
  'entityId',
  'subEntityId',
  'merchantId',
  'rodeoAdvertiserId',
] as const;

/**
 * Query parameter that signals ephemeral ("All Accounts") authorization: when
 * present with no entity-selecting param, BigTopContextService does not resolve
 * a last-used/default advertising account, and Trim/BTAS suppress the current
 * entity (weblab-gated). See DTXINTAKE-314 / V2323372683.
 */
export const EPHEMERAL_ADVERTISER_PARAM = 'ephemeralAdvertiser';

/**
 * Builds the All Accounts URL by stripping entity-related query parameters from
 * the current page URL and adding `ephemeralAdvertiser=true`. Preserves all
 * other params (date range, filters, etc.).
 *
 * The ephemeral param is what carries the "All Accounts" signal to the backend:
 * for the account-switcher/nav init XHRs the browser sends this page URL as the
 * `Referer`, which Trim reads into `ChromeContext.FULL_URL`, and BigTopContextService
 * reads it directly — so neither resolves a stale last-used entity for the page.
 */
export const buildAllAccountsUrl = (): string => {
  const url = new URL(window.location.href);
  for (const param of ENTITY_CONTEXT_PARAMS) {
    url.searchParams.delete(param);
  }
  url.searchParams.set(EPHEMERAL_ADVERTISER_PARAM, 'true');
  const search = url.searchParams.toString();
  return url.pathname + (search ? `?${search}` : '');
};
